import _ from 'lodash'
import {
    QUOTE_MODEL,
    USER_ENTERED_MATERIAL,
} from '@Builder/QuoteDetailPage/constants'
import { TableDataType } from '@Builder/QuoteDetailPage/selectors'
import {
    TableDataTypeAttachment,
    TableDataTypeModel,
} from '@Builder/QuoteDetailPage/types'

import {
    QuoteDetails_Quote_QuoteModels_Item_SelectedQuoteModelRevisionFragment as SelectedQuoteModelRevision,
    QuoteDetails_Quote_QuoteModels_Item_SelectedQuoteModelRevision_ModelConfigurationFragment as QuoteModelConfiguration,
    AdminQuoteModelRevisionDataFragment,
    AdminModelConfigurationDeprecatedFragment,
} from '~/gql/graphql'
import {
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_modelConfiguration as CoreQuoteModelConfiguration,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_processesDetails_totalPrice_ExactPrice as ExactPrice,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_processesDetails_totalPrice_EstimatedPrice as EstimatedPrice,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision,
    coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_modelConfiguration,
} from '~/redux-modules/query-refactor/__generated__/coreQuoteDetails'

type ModelConfiguration = QuoteModelConfiguration | CoreQuoteModelConfiguration
type SelectedRevision = TableDataTypeModel['selectedQuoteModelRevision']
type PostProcess = NonNullable<
    SelectedRevision['processesDetails']
>['postProcesses'][0]

export function getQuoteModelRevisionAttachment(
    quoteModelRevision?:
        | SelectedQuoteModelRevision
        | AdminQuoteModelRevisionDataFragment
        | coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision,
) {
    return quoteModelRevision?.attachmentLink?.quoteAttachmentRevision
}

export function getSelectedQuoteModelRevisions(
    rowData: TableDataType[],
    selectedRowKeys: string[],
) {
    return rowData.reduce<SelectedRevision[]>((memo, { key, item }) => {
        if (
            item.__typename === QUOTE_MODEL &&
            _.includes(selectedRowKeys, key)
        ) {
            memo.push((item as TableDataTypeModel).selectedQuoteModelRevision)
        }
        return memo
    }, [])
}

export function doesConfigMatchForAllQmrs(
    quoteModelRevisions: SelectedRevision[],
) {
    const config = quoteModelRevisions[0]?.modelConfiguration
    const fields: (keyof ModelConfiguration)[] = [
        'process',
        // there are no such fields in quote details queries
        // 'postProcess',
        // 'postProcessThickness',
        'material',
        'color',
        'shore',
        'texture',
    ]

    let match = true

    quoteModelRevisions.forEach(qmr => {
        let configsMatch = true
        const qmrConfig = qmr.modelConfiguration

        fields.forEach(field => {
            const qmrField = qmrConfig?.[field]

            if (
                config &&
                config[field]?.id &&
                qmrField?.id &&
                config[field]?.id !== qmrField?.id
            ) {
                configsMatch = false
            }
        })

        if (config?.quantity !== qmrConfig?.quantity) {
            configsMatch = false
        }

        if (!configsMatch) {
            match = false
        }
    })

    return match ? quoteModelRevisions[0] : null
}

export const extractPostProcessesForForm = (postProcesses: PostProcess[]) => {
    const ppValues: Record<string, string | null> = {}

    postProcesses.forEach(postProcess => {
        _.assign(ppValues, {
            [`${`${postProcess.postProcess.id}-` || ''}postProcessColorId`]:
                postProcess.postProcessColor?.id || null,
            [`${`${postProcess.postProcess.id}-` || ''}postProcessThicknessId`]:
                postProcess.postProcessThickness?.id || null,
        })
    })

    return ppValues
}

export const getInitialConfig = (
    quoteModelRevision: SelectedRevision | AdminQuoteModelRevisionDataFragment,
) => {
    const modelConfig = quoteModelRevision?.modelConfiguration
    const postProcesses = quoteModelRevision?.processesDetails?.postProcesses
    const ppValues = extractPostProcessesForForm(postProcesses || [])

    return {
        processId: modelConfig?.process?.id,
        // model config doesn't have postProcess or postProcessThickness properties
        // postProcessId: modelConfig?.postProcess?.id,
        // postProcessColor: modelConfig?.postProcessColor?.id,
        // postProcessThickness: modelConfig?.postProcessThickness?.id,
        materialId: modelConfig?.material?.id,
        colorId: modelConfig?.color?.id,
        shoreId: (modelConfig as SelectedRevision['modelConfiguration'])?.shore
            ?.id,
        textureId: modelConfig?.texture?.id,
        priorityId: modelConfig?.productionLeadTime?.priorityId,
        quantity: modelConfig?.quantity || 1,
        materialGrade: modelConfig?.materialGrade,
        colorCode: modelConfig?.colorCode,
        textureCode: modelConfig?.textureCode,
        requiresOvermold: modelConfig?.requiresOvermold,
        ...ppValues,
    }
}

export function isModelConfigComplete(
    modelConfig?:
        | SelectedQuoteModelRevision['modelConfiguration']
        | AdminModelConfigurationDeprecatedFragment
        | coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_modelConfiguration,
) {
    return [
        modelConfig,
        modelConfig?.process?.id,
        modelConfig?.material?.id,
        modelConfig?.quantity,
    ].every(item => !(item === undefined || item === null))
}

export const isConfigured = (
    quoteModelRevision?:
        | SelectedQuoteModelRevision
        | AdminQuoteModelRevisionDataFragment
        | coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision
        | null,
) => isModelConfigComplete(quoteModelRevision?.modelConfiguration)

export const isConfiguredWithCustomMaterial = (
    quoteModelRevision?: SelectedQuoteModelRevision,
) =>
    quoteModelRevision?.modelConfiguration?.material?.__typename ===
    USER_ENTERED_MATERIAL

export const getQuoteModelRevisionName = (record: TableDataType) => {
    const isAQuoteModel = record.item.__typename === QUOTE_MODEL
    const quoteModelName = (record.item as TableDataTypeModel)
        .selectedQuoteModelRevision?.partRevision?.modelFileName
    const quoteAttachmentName = (record.item as TableDataTypeAttachment)
        .latestAttachmentRevision?.attachment?.fileName

    return isAQuoteModel ? quoteModelName : quoteAttachmentName
}

const getModelConfiguration = (record: TableDataType) =>
    (record.item as TableDataTypeModel).selectedQuoteModelRevision
        ?.modelConfiguration

export const getProcessName = (record: TableDataType) =>
    getModelConfiguration(record)?.process?.name || ''

export const getProductionDays = (record: TableDataType) =>
    getModelConfiguration(record)?.productionLeadTime?.productionDays ||
    Infinity

export const getQuantity = (record: TableDataType) =>
    getModelConfiguration(record)?.quantity || 0

export const getPriceInCents = (record: TableDataType) => {
    const recordItem = record.item as TableDataTypeModel
    const totalPrice =
        recordItem.selectedQuoteModelRevision?.processesDetails?.totalPrice
    const priceInCents = (totalPrice as ExactPrice)?.priceInCents
    const lowPriceInCents = (totalPrice as EstimatedPrice)?.lowPriceInCents

    return typeof priceInCents === 'number' ? priceInCents : lowPriceInCents
}

export const isAConfiguredQuoteModel = (record: TableDataType) => {
    const isAQuoteModel = record.item.__typename === QUOTE_MODEL
    const modelIsConfigured = getModelConfiguration(record)

    return !!(isAQuoteModel && modelIsConfigured)
}
